import { create } from '@bufbuild/protobuf'
import _ from 'lodash'
import { useMemo } from 'react'

import { useInviteRecipient, useRemoveRecipients } from '@/api/assessor-questionnaire.hook'
import { useGetPointsOfContact, useListEngagements } from '@/api/company.hook'
import { useGetPortalReminderSetting } from '@/api/notifications-service.hook'
import { Company } from '@/gen/inventory/v1/company_service_pb'
import { CompanyOrEngagementSchema } from '@/gen/inventory/v1/entity_pb'

import { useInviteRecipientModal } from '@/modals/invite-recipient-modal/use-invite-recipient-modal.hook'

type UseQuestionnaireInviteRecipientProps = {
  company: Company
}

export const useQuestionnaireInviteRecipient = ({
  company,
}: UseQuestionnaireInviteRecipientProps) => {
  const companyId = company.id
  const companyName = company.profile?.name ?? ''
  const recipientsEmails = company.recipientsEmails

  const companyEntity = create(CompanyOrEngagementSchema, {
    entity: { case: 'companyId', value: companyId },
  })
  const { data: companyPocData } = useGetPointsOfContact(companyEntity)

  const { data: engagementsData } = useListEngagements()
  const companyEngagements = useMemo(
    () => (engagementsData?.engagements || []).filter((eng) => eng.orgCompanyId === companyId),
    [engagementsData, companyId],
  )

  const { data: portalReminderSetting } = useGetPortalReminderSetting()
  const businessOwner = company.businessOwner
  const businessOwnerCc =
    portalReminderSetting?.ccBusinessOwnerEnabled && businessOwner?.email
      ? { name: businessOwner.name, email: businessOwner.email, thirdPartyName: companyName }
      : undefined

  const { mutateAsync: inviteRecipient, isPending: isInvitingRecipient } = useInviteRecipient()
  const { mutateAsync: removeRecipients, isPending: isRemovingRecipients } = useRemoveRecipients()

  const suggestRecipientsGroups = useMemo(() => {
    const groups: { groupName: string; groupMembers: { email: string; name: string }[] }[] = []

    // Company-level members: business owner + points of contact
    const companyMembers = [
      ...(company.businessOwner
        ? [{ email: company.businessOwner.email, name: company.businessOwner.name }]
        : []),
      ...(companyPocData?.pointOfContacts
        ?.filter((poc) => poc.active)
        .map((poc) => ({
          email: poc.email,
          name: `${poc.name} ${poc.role ? `(${poc.role})` : ''}`,
        })) ?? []),
    ]

    const uniqueCompanyMembers = _.uniqBy(companyMembers, 'email')
    if (uniqueCompanyMembers.length > 0) {
      groups.push({
        groupName: `${companyName} - Points of contact`,
        groupMembers: uniqueCompanyMembers,
      })
    }

    // Engagement-level groups: business owner + points of contact for each engagement
    for (const engagement of companyEngagements) {
      const engagementMembers = [
        ...(engagement.businessOwner
          ? [{ email: engagement.businessOwner.email, name: engagement.businessOwner.name }]
          : []),
        ...(engagement.pointOfContacts
          ?.filter((poc) => poc.active)
          .map((poc) => ({
            email: poc.email,
            name: `${poc.name} ${poc.role ? `(${poc.role})` : ''}`,
          })) ?? []),
      ]

      const uniqueEngagementMembers = _.uniqBy(engagementMembers, 'email')
      if (uniqueEngagementMembers.length > 0) {
        groups.push({
          groupName: `${engagement.name} - Points of contact`,
          groupMembers: uniqueEngagementMembers,
        })
      }
    }

    return groups
  }, [company, companyPocData, companyEngagements, companyName])

  const { renderModal: InviteRecipientModal, openModal: openInviteRecipientModal } =
    useInviteRecipientModal({
      isLoading: isInvitingRecipient || isRemovingRecipients,
      onRemoveRecipient: async (recipientId: string) => {
        await removeRecipients({
          emails: [recipientId],
          orgCompanyId: companyId,
        })
      },
      onAddRecipient: async (recipientEmails: string[]) => {
        await inviteRecipient({
          emails: recipientEmails,
          orgCompanyId: companyId,
        })
      },
      recipientEmails: recipientsEmails,
      suggestRecipientsGroups,
      businessOwnerCc,
    })

  return { InviteRecipientModal, openInviteRecipientModal }
}
